import React, { Fragment, useState, useEffect, Component } from 'react';
import PropTypes from 'prop-types';
import './countdown-timer.style.scss';
import classNames from 'classnames';
import { formatDate } from '@thd-olt-functional/utils';
import helpers from '../helpers/countdownHelper';

const CountdownTimer = ({
  endDate,
  timerDuration,
  timerThreshold = 3,
  hours,
  minutes,
  shouldShowSecondsRemaining,
  shouldUseMessagingString,
  shouldUseClockDisplay,
  shouldUseDateDisplay,
  shouldUseDaysLeftDisplay,
  isSBOTD
}) => {

  const initializeEndDate = () => {
    if (endDate) {
      return new Date(endDate);
    }
    if (hours || minutes) {
      const tempDate = new Date();
      if (hours) {
        tempDate.setHours(tempDate.getHours() + +hours);
      }
      if (minutes) {
        tempDate.setMinutes(tempDate.getMinutes() + +minutes);
      }
      tempDate.setSeconds(59);
      return tempDate;
    }
    return 0;
  };

  const [timerEndDate, setTimerEndDate] = useState(initializeEndDate());

  const calculateTimeLeft = () => {
    if (!timerEndDate) {
      return 0;
    }
    let newTimeLeft = null;
    if (timerEndDate && isSBOTD) {
      newTimeLeft = helpers.calculateSbotdTimeLeft();
    }
    if (!isSBOTD) {
      const timeToExpiration = +new Date(timerEndDate) - +new Date();

      if (timeToExpiration > 0) {
        newTimeLeft = {
          days: Math.floor(timeToExpiration / (1000 * 60 * 60 * 24)),
          timerHours: Math.floor((timeToExpiration / (1000 * 60 * 60)) % 24),
          timerMinutes: Math.floor((timeToExpiration / 1000 / 60) % 60),
          seconds: Math.floor((timeToExpiration / 1000) % 60)
        };
      }
    }

    return newTimeLeft;
  };

  const [timeLeft, setTimeLeft] = useState(calculateTimeLeft());

  const getTimeStringDate = () => {

    return formatDate({ date: timerEndDate, shortMonth: true, dayMonthDateFormat: true });
  };

  const getTimeStringDaysHoursMinutes = () => {

    const daySuffix = (timeLeft || {}).days === 1 ? ' day' : ' days';
    const hourSuffix = (timeLeft || {}).timerHours === 1 ? ' hr' : ' hours';
    const minuteSuffix = (timeLeft || {}).timerMinutes === 1 ? ' min' : ' mins';
    const secondSuffix = (timeLeft || {}).seconds === 1 ? ' sec' : ' secs';

    const days = timeLeft?.days ? `${timeLeft.days}${(daySuffix)}` : '';
    const timerHours = timeLeft?.timerHours ? ` ${timeLeft.timerHours}${(hourSuffix)}` : '';
    const timerMinutes = timeLeft?.timerMinutes ? ` ${timeLeft.timerMinutes}${(minuteSuffix)}` : '';
    const timerSeconds = shouldShowSecondsRemaining && timeLeft?.seconds ? ` ${timeLeft.seconds}${(secondSuffix)}` : '';
    return `${days}${timerHours}${timerMinutes}${timerSeconds}`;
  };

  const getTimeStringDigitalClock = () => {

    let newTimeString = '';

    const { days, timerHours, timerMinutes, seconds } = timeLeft;
    /* eslint-disable max-len */
    newTimeString += `${((days * 24 + timerHours) >= 10 ? days * 24 + timerHours : '0'.concat(timerHours))}`;
    newTimeString += `:${(timerMinutes >= 10 ? timerMinutes : '0'.concat(timerMinutes))}`;
    newTimeString += `:${(seconds >= 10 ? seconds : '0'.concat(seconds))}`;
    /* eslint-enable max-len */
    return newTimeString;
  };

  const getTimeStringDaysLeft = () => {

    let newTimeString = '';

    newTimeString += `${timeLeft.days}${(timeLeft.days === 1 ? ' day' : ' days')}`;
    return newTimeString;
  };

  const getTimeStringAndMessaging = () => {

    if (isSBOTD && shouldShowSecondsRemaining && timeLeft) {
      return { timeString: timeLeft };
    }
    if (!shouldUseClockDisplay && !shouldUseDateDisplay) {
      return { messagingString: 'Ends in ', timeString: getTimeStringDaysHoursMinutes() };
    }

    if (shouldUseDateDisplay && timerEndDate && timeLeft
      && (timeLeft.days > timerThreshold || !shouldUseClockDisplay)) {
      return { messagingString: 'Ends on ', timeString: getTimeStringDate() };
    }

    if (shouldUseClockDisplay && timeLeft && timeLeft.days <= timerThreshold) {
      if (timeLeft.days >= 1 && shouldUseDaysLeftDisplay) {
        return { messagingString: 'Ends in ', timeString: getTimeStringDaysLeft() };
      }
      return { messagingString: 'Ends in ', timeString: getTimeStringDigitalClock() };
    }

    return {};
  };

  const [timeStringAndMessaging, setTimeStringAndMessaging] = useState(getTimeStringAndMessaging());

  useEffect(() => {

    const timeout = setTimeout(() => {
      setTimeLeft(calculateTimeLeft());
      setTimeStringAndMessaging((getTimeStringAndMessaging()));
    }, 1000);

    return () => {
      clearTimeout(timeout);
    };
  }, [timeLeft]);

  if (!(timerEndDate && timeLeft && timeStringAndMessaging)) {
    return null;
  }

  const timerClasses = classNames('countdown-timer__wrapper', {
    'countdown-timer__wrapper--verbose': shouldUseMessagingString
  });

  return (
    <span className={timerClasses}>
      {timeStringAndMessaging.messagingString && shouldUseMessagingString && (
        <span className={timerClasses}>
          {timeStringAndMessaging.messagingString}
        </span>
      )}
      {timeStringAndMessaging.timeString && (
        timeStringAndMessaging.timeString
      )}
    </span>
  );
};

CountdownTimer.propTypes = {
  endDate: PropTypes.oneOfType([PropTypes.string, PropTypes.shape({})]),
  timerDuration: PropTypes.number,
  timerThreshold: PropTypes.number,
  hours: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  minutes: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  shouldUseMessagingString: PropTypes.bool,
  shouldShowSecondsRemaining: PropTypes.bool,
  shouldUseClockDisplay: PropTypes.bool,
  shouldUseDateDisplay: PropTypes.bool,
  shouldUseDaysLeftDisplay: PropTypes.bool,
  isSBOTD: PropTypes.bool
};

CountdownTimer.defaultProps = {
  endDate: null,
  timerDuration: 0,
  timerThreshold: 3,
  hours: 0,
  minutes: 0,
  shouldUseMessagingString: false,
  shouldShowSecondsRemaining: false,
  shouldUseClockDisplay: false,
  shouldUseDateDisplay: false,
  shouldUseDaysLeftDisplay: false,
  isSBOTD: false
};

export default CountdownTimer;
